import React, { useEffect, useState } from 'react';
import { loanTypesAPI } from '../services/api';
import './ManagementPages.css';

const emptyForm = {
  name: '',
  description: '',
  amount: '',
  tenure_days: 60,
  daily_emi: '',
  recover_amount: '',
  is_active: true,
};

const LoanTypesPage = () => {
  const [loanTypes, setLoanTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [computedDailyEmi, setComputedDailyEmi] = useState(0);

  useEffect(() => {
    fetchLoanTypes();
  }, []);

  const fetchLoanTypes = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { page: 1, limit: 100 };
      const resp = await loanTypesAPI.list(params);
      // API may return array or a paginated object. Handle multiple shapes and normalize.
      let list = [];
      if (!resp) list = [];
      else if (Array.isArray(resp)) list = resp;
      else if (Array.isArray(resp.data)) list = resp.data;
      else if (Array.isArray(resp.data?.data)) list = resp.data.data;
      else if (Array.isArray(resp.loan_types)) list = resp.loan_types;
      else list = resp.data || [];

      // Normalize fields so frontend always sees consistent keys
      const normalized = list.map((lt) => {
        const daily = Number(lt.daily_emi ?? lt.dailyEmi ?? 0);
        const tenure = Number(lt.tenure_days ?? lt.tenureDays ?? 0) || 0;
        let recover = lt.recover_amount ?? lt.recoverAmount;
        if ((recover === undefined || recover === null || Number(recover) === 0) && daily > 0 && tenure > 0) {
          recover = Math.round(daily * tenure * 100) / 100;
        }

        return {
          ...lt,
          recover_amount: recover ?? 0,
          daily_emi: daily,
          min_amount: lt.min_amount ?? lt.minAmount ?? 0,
          max_amount: lt.max_amount ?? lt.maxAmount ?? 0,
          tenure_days: tenure || 60,
          is_active: lt.is_active ?? lt.isActive ?? true,
        };
      });

      // Only show active loan types
      setLoanTypes(normalized.filter(lt => lt.is_active !== false));
    } catch (err) {
      setError(err.message || 'Failed to load loan types');
    } finally {
      setLoading(false);
    }
  };

  const handleInput = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  // helper: compute daily EMI based on recover amount only
  const calculateDailyEMI = (principal, tenureDays, recoverAmount = 0) => {
    const t = Number(tenureDays || 60);
    const r = Number(recoverAmount || 0);
    if (t <= 0) return 0;

    const emi = r / t;
    return Math.round(emi * 100) / 100;
  };

  // Recompute daily EMI whenever amount or interest rate changes
  useEffect(() => {
    const amt = Number(form.amount || 0);
    const recover = Number(form.recover_amount || 0);
    const tenure = Number(form.tenure_days || 60) || 60;
    if (amt > 0) {
      const emi = calculateDailyEMI(amt, tenure, recover);
      setComputedDailyEmi(emi);
    } else {
      setComputedDailyEmi(0);
    }
  }, [form.amount, form.recover_amount, form.tenure_days]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = async (lt) => {
    try {
      setLoading(true);
      // Fetch fresh loan type from server to ensure recover_amount/daily_emi are current
      const resp = await loanTypesAPI.get(lt.id);
      const payload = resp && (resp.data || resp) ? (resp.data || resp) : lt;

      // Normalize payload fields and derive recover_amount from daily_emi*tenure if missing
      const dailyVal = Number(payload.daily_emi ?? payload.dailyEmi ?? 0);
      const tenureVal = Number(payload.tenure_days ?? payload.tenureDays ?? 0) || 0;
      let recoverVal = payload.recover_amount ?? payload.recoverAmount;
      if ((recoverVal === undefined || recoverVal === null || Number(recoverVal) === 0) && dailyVal > 0 && tenureVal > 0) {
        recoverVal = Math.round(dailyVal * tenureVal * 100) / 100;
      }

      const ltFresh = {
        ...payload,
        recover_amount: recoverVal ?? 0,
        daily_emi: dailyVal,
        min_amount: payload.min_amount ?? payload.minAmount ?? 0,
        max_amount: payload.max_amount ?? payload.maxAmount ?? 0,
        tenure_days: tenureVal || 60,
        is_active: payload.is_active ?? payload.isActive ?? true,
      };

      setEditing(ltFresh);
      setForm({
        name: ltFresh.name || '',
        description: ltFresh.description || '',
        amount: (ltFresh.min_amount && ltFresh.min_amount === ltFresh.max_amount) ? ltFresh.min_amount : (ltFresh.min_amount || ltFresh.max_amount || ''),
        tenure_days: ltFresh.tenure_days || 60,
        daily_emi: ltFresh.daily_emi || '',
        // prefill recover_amount as string so input shows the value
        recover_amount: ltFresh.recover_amount !== undefined && ltFresh.recover_amount !== null ? String(ltFresh.recover_amount) : '',
        is_active: ltFresh.is_active === undefined ? true : ltFresh.is_active,
      });
      setShowForm(true);
    } catch (err) {
      alert('Failed to fetch loan type: ' + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Frontend validation
    const amountVal = Number(form.amount);
    const tenureDays = Number(form.tenure_days || 60);
    const dailyEmi = Number(form.daily_emi);
    if (!form.name) {
      alert('Name is required.');
      return;
    }
    if (amountVal <= 0) {
      alert('Amount must be a positive number.');
      return;
    }
    // enforce fixed tenure of 60 days
    if (tenureDays !== 60) {
      alert('Tenure is fixed to 60 days.');
      return;
    }
    let computedDailyEmi = dailyEmi;
    const recoverAmountVal = Number(form.recover_amount) || 0;
    // helper: same calculation as backend
      // compute daily EMI from recover amount
      if (!computedDailyEmi || computedDailyEmi <= 0) {
        try {
          computedDailyEmi = calculateDailyEMI(amountVal, tenureDays, recoverAmountVal);
        } catch (err) {
          alert('Unable to compute daily EMI: ' + err.message);
          return;
        }
      }
    try {
      // Map frontend fields to backend expected fields
      // Backend still expects minAmount/maxAmount; use same value for both
      const payload = {
        name: form.name,
        description: form.description,
        minAmount: amountVal,
        maxAmount: amountVal,
        tenureDays,
        dailyEmi: computedDailyEmi,
        // send numeric snake_case to backend
        recover_amount: Number(form.recover_amount) || 0,
        isActive: !!form.is_active,
      };
      if (editing) {
        await loanTypesAPI.update(editing.id, payload);
        alert('Loan type updated');
      } else {
        await loanTypesAPI.create(payload);
        alert('Loan type created');
      }
      setShowForm(false);
      fetchLoanTypes();
    } catch (err) {
      // Show backend error message if available
      if (err && err.message) {
        alert('Error: ' + err.message);
      } else {
        alert('An unexpected error occurred. Please check your input and try again.');
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this loan type?')) return;
    try {
      await loanTypesAPI.delete(id);
      fetchLoanTypes();
    } catch (err) {
      alert('Delete error: ' + (err.message || err));
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Loan Types</h1>
        <p>Manage predefined loan products used for loan submissions.</p>
      </div>

      <div style={{ marginBottom: 16 }}>
        <button className="button button-primary" onClick={openCreate}>Add Loan Type</button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div className="loader"></div>
      ) : (
        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Amount</th>
                <th>Tenure (days)</th>
                <th>Daily EMI</th>
                <th>Recover Amount</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loanTypes.map((lt) => (
                <tr key={lt.id}>
                  <td>{lt.name}</td>
                    <td>₹{Number((lt.min_amount && lt.min_amount === lt.max_amount) ? lt.min_amount : (lt.min_amount || lt.max_amount || 0)).toLocaleString()}</td>
                    <td>{lt.tenure_days}</td>
                  <td>₹{Number(lt.daily_emi || 0).toLocaleString()}</td>
                  <td>₹{Number(lt.recover_amount || 0).toLocaleString()}</td>
                  <td>{lt.is_active ? 'Yes' : 'No'}</td>
                  <td>
                    <button className="action-btn edit-btn" onClick={() => openEdit(lt)}>Edit</button>
                    <button className="action-btn delete-btn" onClick={() => handleDelete(lt.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editing ? 'Edit Loan Type' : 'Add Loan Type'}</h2>
              <button className="close-btn" onClick={() => setShowForm(false)}>×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Name</label>
                  <input name="name" value={form.name} onChange={handleInput} required />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea name="description" value={form.description} onChange={handleInput} />
                </div>
                <div className="form-group">
                  <label>Amount</label>
                  <input name="amount" type="number" value={form.amount} onChange={handleInput} required />
                </div>
                <div className="form-group">
                  <label>Tenure (days)</label>
                  <input name="tenure_days" type="number" value={form.tenure_days} onChange={handleInput} required readOnly />
                </div>
                <div className="form-group">
                  <label>Daily EMI (calculated)</label>
                  <input name="daily_emi" type="number" value={computedDailyEmi} readOnly />
                </div>
                <div className="form-group">
                  <label>Recover Amount (absolute)</label>
                  <input name="recover_amount" type="number" step="0.01" value={form.recover_amount} onChange={handleInput} required />
                </div>
                <div className="form-group">
                  <label>
                    <input name="is_active" type="checkbox" checked={form.is_active} onChange={handleInput} /> Active
                  </label>
                </div>
                <div className="form-actions">
                  <button type="submit" className="button button-primary">{editing ? 'Update' : 'Create'}</button>
                  <button type="button" className="button button-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoanTypesPage;
